import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, BookOpen, Play, X, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

const STORAGE_PREFIX = "chizy_onboarding_dismissed_";
const FORCE_OPEN_KEY = "chizy_onboarding_force_open";

export default function HelpFAB() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [trainingVideoUrl, setTrainingVideoUrl] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/ops/training-video")
      .then((res) => setTrainingVideoUrl(res.data?.video_url || null))
      .catch(() => setTrainingVideoUrl(null));
  }, [user]);

  if (!user) return null;

  const reopenTutorial = () => {
    setOpen(false);
    // El proveedor tiene su propia pantalla de bienvenida (con el video guía).
    if (user.role === "supplier") {
      navigate("/bienvenida-proveedor");
      return;
    }
    localStorage.removeItem(STORAGE_PREFIX + user.role);
    localStorage.setItem(FORCE_OPEN_KEY, Date.now().toString());
    window.dispatchEvent(new Event("chizy:reopen-onboarding"));
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            data-testid="help-fab-menu"
            className="fixed bottom-20 right-6 z-50 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
          >
            <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] text-white p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <div className="font-bold text-sm">{t("help.needHelp")}</div>
                <div className="text-[11px] text-slate-300">{t("help.stepByStep")}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="ml-auto w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
                aria-label={t("common.close")}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-3 space-y-1">
              <button
                data-testid="reopen-tutorial-btn"
                onClick={reopenTutorial}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50"
              >
                <div className="w-8 h-8 rounded-lg bg-[#FFFBE6] border border-[#FFDF00] flex items-center justify-center flex-shrink-0">
                  <Play className="w-4 h-4 text-[#1A1A1A]" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#1A1A1A]">{t("help.watchAgain")}</div>
                  <div className="text-[11px] text-slate-500">{t("help.watchAgainSub")}</div>
                </div>
              </button>

              {trainingVideoUrl && (
                <a
                  href={trainingVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="help-training-video-link"
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50"
                  onClick={() => setOpen(false)}
                >
                  <div className="w-8 h-8 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
                    <Video className="w-4 h-4 text-red-600" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#1A1A1A]">{t("help.watchTraining")}</div>
                    <div className="text-[11px] text-slate-500">{t("help.watchTrainingSub")}</div>
                  </div>
                </a>
              )}

              <a
                href="https://wa.me/5215555555555?text=Hola%2C%20tengo%20una%20duda%20del%20sistema%20Chizy%20Chiz"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="help-whatsapp"
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50"
                onClick={() => setOpen(false)}
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4 text-emerald-700" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#1A1A1A]">{t("help.contactSupport")}</div>
                  <div className="text-[11px] text-slate-500">{t("help.contactSupportSub")}</div>
                </div>
              </a>
            </div>
            <div className="bg-slate-50 px-4 py-2 border-t border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              {t("help.footer")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="help-fab-btn"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#FFDF00] shadow-lg shadow-[#FFDF00]/40 hover:shadow-xl flex items-center justify-center group"
        aria-label={t("help.needHelp")}
      >
        <HelpCircle className="w-5 h-5 text-[#1A1A1A]" strokeWidth={2.5} />
        <span className="absolute inset-0 rounded-full ring-2 ring-[#FFDF00]/40 animate-ping opacity-0 group-hover:opacity-100"></span>
      </motion.button>
    </>
  );
}
